import {
  lazy,
  Suspense,
  type ComponentType,
} from "react";
import { useQuery } from "@tanstack/react-query";
import { Navigate, Route, Routes } from "react-router-dom";
import { Button, PageError, PageLoading, Toaster } from "@lumika/ui";
import { adminApi } from "./api/admin";
import { ApiError } from "./api/client";
import { AdminShell } from "./layouts/AdminShell";
import { SignInPage } from "./pages/SignInPage";
import { LegacyRouteRedirect } from "./components/LegacyRouteRedirect";

// Admin routes are operationally independent. Give each one a local async
// boundary so chart-heavy Billing and infrequently used setup tools are not
// part of every administrator's first download.
function lazyRoute<TModule>(
  loader: () => Promise<TModule>,
  select: (module: TModule) => ComponentType,
) {
  const Component = lazy(async () => ({ default: select(await loader()) }));

  return function DeferredRoute() {
    return (
      <Suspense fallback={<RoutePending />}>
        <Component />
      </Suspense>
    );
  };
}

const ChannelsPlaygroundLayout = lazyRoute(
  () => import("./layouts/ChannelsPlaygroundLayout"),
  (module) => module.ChannelsPlaygroundLayout,
);
const DealershipScope = lazyRoute(
  () => import("./layouts/DealershipScope"),
  (module) => module.DealershipScope,
);
const DealershipInboxLayout = lazyRoute(
  () => import("./layouts/DealershipInboxLayout"),
  (module) => module.DealershipInboxLayout,
);
const DealershipActivityPage = lazyRoute(
  () => import("./pages/dealership/DealershipActivityPage"),
  (module) => module.DealershipActivityPage,
);
const DealershipAiPage = lazyRoute(
  () => import("./pages/dealership/DealershipAiPage"),
  (module) => module.DealershipAiPage,
);
const DealershipBillingPage = lazyRoute(
  () => import("./pages/dealership/DealershipBillingPage"),
  (module) => module.DealershipBillingPage,
);
const DealershipCalendarPage = lazyRoute(
  () => import("./pages/dealership/DealershipCalendarPage"),
  (module) => module.DealershipCalendarPage,
);
const DealershipChannelsPage = lazyRoute(
  () => import("./pages/dealership/DealershipChannelsPage"),
  (module) => module.DealershipChannelsPage,
);
const DealershipConnectionsPage = lazyRoute(
  () => import("./pages/dealership/DealershipConnectionsPage"),
  (module) => module.DealershipConnectionsPage,
);
const DealershipIntegrationsPage = lazyRoute(
  () => import("./pages/dealership/DealershipIntegrationsPage"),
  (module) => module.DealershipIntegrationsPage,
);
const DealershipContactDetailPage = lazyRoute(
  () => import("./pages/dealership/DealershipContactDetailPage"),
  (module) => module.DealershipContactDetailPage,
);
const DealershipContactsPage = lazyRoute(
  () => import("./pages/dealership/DealershipContactsPage"),
  (module) => module.DealershipContactsPage,
);
const DealershipContactFormPage = lazyRoute(
  () => import("./pages/dealership/DealershipContactsPage"),
  (module) => module.DealershipContactFormPage,
);
const DealershipLeadDetailPage = lazyRoute(
  () => import("./pages/dealership/DealershipLeadDetailPage"),
  (module) => module.DealershipLeadDetailPage,
);
const DealershipInventoryImportsPage = lazyRoute(
  () => import("./pages/dealership/DealershipInventoryImportsPage"),
  (module) => module.DealershipInventoryImportsPage,
);
const DealershipInventoryImportDetailPage = lazyRoute(
  () => import("./pages/dealership/DealershipInventoryImportsPage"),
  (module) => module.DealershipInventoryImportDetailPage,
);
const DealershipInventoryPage = lazyRoute(
  () => import("./pages/dealership/DealershipInventoryPage"),
  (module) => module.DealershipInventoryPage,
);
const DealershipInventoryFormPage = lazyRoute(
  () => import("./pages/dealership/DealershipInventoryPage"),
  (module) => module.DealershipInventoryFormPage,
);
const DealershipOverviewPage = lazyRoute(
  () => import("./pages/dealership/DealershipOverviewPage"),
  (module) => module.DealershipOverviewPage,
);
const DealershipRooftopsPage = lazyRoute(
  () => import("./pages/dealership/DealershipRooftopsPage"),
  (module) => module.DealershipRooftopsPage,
);
const DealershipLocationDetailPage = lazyRoute(
  () => import("./pages/dealership/DealershipRooftopsPage"),
  (module) => module.DealershipLocationDetailPage,
);
const DealershipTelephonyPage = lazyRoute(
  () => import("./pages/dealership/DealershipTelephonyPage"),
  (module) => module.DealershipTelephonyPage,
);
const A2pWizardPage = lazyRoute(
  () => import("./pages/dealership/A2pWizardPage"),
  (module) => module.A2pWizardPage,
);
const DealershipUsersPage = lazyRoute(
  () => import("./pages/dealership/DealershipUsersPage"),
  (module) => module.DealershipUsersPage,
);
const DealershipTeamMemberPage = lazyRoute(
  () => import("./pages/dealership/DealershipUsersPage"),
  (module) => module.DealershipTeamMemberPage,
);
const TelephonyAccountDetailPage = lazyRoute(
  () => import("./pages/dealership/TelephonyAccountDetailPage"),
  (module) => module.TelephonyAccountDetailPage,
);
const DealershipsLandingPage = lazyRoute(
  () => import("./pages/DealershipsLandingPage"),
  (module) => module.DealershipsLandingPage,
);
const NewDealershipPage = lazyRoute(
  () => import("./pages/NewDealershipPage"),
  (module) => module.NewDealershipPage,
);
const ContractsPage = lazyRoute(
  () => import("./pages/contracts/ContractsPage"),
  (module) => module.ContractsPage,
);
const NewContractPage = lazyRoute(
  () => import("./pages/contracts/NewContractPage"),
  (module) => module.NewContractPage,
);
const ContractDetailPage = lazyRoute(
  () => import("./pages/contracts/ContractDetailPage"),
  (module) => module.ContractDetailPage,
);
const ContractDealerPage = lazyRoute(
  () => import("./pages/contracts/ContractDealerPage"),
  (module) => module.ContractDealerPage,
);
const OutreachPage = lazyRoute(
  () => import("./pages/outreach/OutreachPage"),
  (module) => module.OutreachPage,
);
const OutreachProspectDetailPage = lazyRoute(
  () => import("./pages/outreach/OutreachProspectDetailPage"),
  (module) => module.OutreachProspectDetailPage,
);
const PlatformBillingPage = lazyRoute(
  () => import("./pages/PlatformBillingPage"),
  (module) => module.PlatformBillingPage,
);
const OpsPage = lazyRoute(
  () => import("./pages/OpsPage"),
  (module) => module.OpsPage,
);
const SftpAccountsPage = lazyRoute(
  () => import("./pages/SftpAccountsPage"),
  (module) => module.SftpAccountsPage,
);
const SftpBrowserPage = lazyRoute(
  () => import("./pages/SftpBrowserPage"),
  (module) => module.SftpBrowserPage,
);
const ChannelsPlaygroundApiPage = lazyRoute(
  () => import("./pages/channels-playground/ChannelsPlaygroundApiPage"),
  (module) => module.ChannelsPlaygroundApiPage,
);
const ChannelsPlaygroundWidgetPage = lazyRoute(
  () => import("./pages/channels-playground/ChannelsPlaygroundWidgetPage"),
  (module) => module.ChannelsPlaygroundWidgetPage,
);

function RoutePending() {
  return (
    <div
      className="flex flex-1 flex-col gap-6 p-4 md:p-6"
      role="status"
      aria-label="Loading page"
    >
      <div className="space-y-3 border-b pb-5">
        <div className="h-3 w-24 rounded-control bg-muted" />
        <div className="h-7 w-48 rounded-control bg-muted" />
      </div>
      <div className="space-y-3" aria-hidden="true">
        <div className="h-11 rounded-control bg-muted" />
        <div className="h-16 rounded-control border bg-surface-subtle" />
        <div className="h-16 rounded-control border bg-surface-subtle" />
        <div className="h-16 rounded-control border bg-surface-subtle" />
      </div>
      <span className="sr-only">Loading page…</span>
    </div>
  );
}

export function App() {
  const meQuery = useQuery({
    queryKey: ["admin", "me"],
    queryFn: adminApi.me,
    retry: false,
  });

  if (meQuery.isLoading) {
    return (
      <div className="grid min-h-screen place-items-center bg-background text-foreground">
        <PageLoading message="Loading lumika admin…" />
      </div>
    );
  }

  // A 401/403 means "signed out" — anything else (500, network blip) is
  // a transient failure that must not masquerade as the sign-in page.
  const authStatus =
    meQuery.error instanceof ApiError ? meQuery.error.status : null;
  if (meQuery.isError && authStatus !== 401 && authStatus !== 403) {
    return (
      <div className="grid min-h-screen place-items-center bg-background text-foreground">
        <PageError
          title="Couldn't load lumika admin"
          description={meQuery.error.message}
          action={
            <Button variant="outline" onClick={() => meQuery.refetch()}>
              Retry
            </Button>
          }
        />
      </div>
    );
  }

  if (!meQuery.data) {
    return <SignInPage />;
  }

  return (
    <>
      <Routes>
        <Route element={<AdminShell admin={meQuery.data.admin} />}>
          <Route index element={<Navigate to="/dealerships" replace />} />
          <Route path="dealerships">
            <Route index element={<DealershipsLandingPage />} />
            <Route path="new" element={<NewDealershipPage />} />
            <Route path=":dealershipId" element={<DealershipScope />}>
              <Route index element={<Navigate to="overview" replace />} />
              <Route path="overview" element={<DealershipOverviewPage />} />
              <Route path="inbox" element={<DealershipInboxLayout />}>
                <Route
                  path=":leadId"
                  element={<DealershipLeadDetailPage />}
                />
              </Route>
              <Route path="contacts">
                <Route index element={<DealershipContactsPage />} />
                <Route path="new" element={<DealershipContactFormPage />} />
                <Route
                  path=":contactId/edit"
                  element={<DealershipContactFormPage />}
                />
                <Route path=":contactId" element={<DealershipContactDetailPage />} />
              </Route>
              <Route path="calendar" element={<DealershipCalendarPage />} />
              {/* Legacy URL — keep the link working for bookmarks. */}
              <Route
                path="appointments"
                element={<LegacyRouteRedirect to="../calendar" />}
              />
              <Route path="inventory" element={<DealershipInventoryPage />} />
              <Route path="inventory/new" element={<DealershipInventoryFormPage />} />
              <Route
                path="inventory/:vin/edit"
                element={<DealershipInventoryFormPage />}
              />
              <Route
                path="inventory-imports"
                element={<DealershipInventoryImportsPage />}
              />
              <Route
                path="inventory-imports/new"
                element={<DealershipInventoryImportDetailPage />}
              />
              <Route
                path="inventory-imports/:importId"
                element={<DealershipInventoryImportDetailPage />}
              />
              {/* SFTP credentials + file browser are global, not
                  per-dealership — see /sftp at the top level. The
                  legacy dealerships/:id/sftp routes were removed when
                  the credential dropped its dealership_id FK. */}
              <Route path="sftp" element={<LegacyRouteRedirect to="/sftp" />} />
              <Route path="locations" element={<DealershipRooftopsPage />} />
              <Route path="locations/new" element={<DealershipLocationDetailPage />} />
              <Route
                path="locations/:rooftopId"
                element={<DealershipLocationDetailPage />}
              />
              <Route
                path="rooftops"
                element={<LegacyRouteRedirect to="../locations" />}
              />
              <Route path="team" element={<DealershipUsersPage />} />
              <Route path="team/new" element={<DealershipTeamMemberPage />} />
              <Route path="team/:userId" element={<DealershipTeamMemberPage />} />
              <Route
                path="users"
                element={<LegacyRouteRedirect to="../team" />}
              />
              <Route path="channels" element={<DealershipChannelsPage />} />
              <Route path="connections" element={<DealershipConnectionsPage />} />
              <Route path="integrations" element={<DealershipIntegrationsPage />} />
              <Route path="tcr" element={<LegacyRouteRedirect to="../telephony" />} />
              <Route path="telephony" element={<DealershipTelephonyPage />} />
              <Route
                path="telephony/:telephonyAccountId/a2p"
                element={<A2pWizardPage />}
              />
              <Route
                path="telephony/:telephonyAccountId/a2p/:step"
                element={<A2pWizardPage />}
              />
              <Route
                path="telephony/:telephonyAccountId"
                element={<TelephonyAccountDetailPage />}
              />
              <Route path="ai" element={<DealershipAiPage />} />
              <Route path="billing" element={<DealershipBillingPage />} />
              <Route
                path="setup"
                element={<LegacyRouteRedirect to="../connections" />}
              />
              <Route path="activity" element={<DealershipActivityPage />} />
            </Route>
          </Route>
          <Route path="contracts">
            <Route index element={<ContractsPage />} />
            <Route path="new" element={<NewContractPage />} />
            <Route path="dealers/:dealerId" element={<ContractDealerPage />} />
            <Route path=":contractId" element={<ContractDetailPage />} />
          </Route>
          <Route path="outreach">
            <Route index element={<OutreachPage />} />
            <Route path=":prospectId" element={<OutreachProspectDetailPage />} />
          </Route>
          <Route path="billing" element={<PlatformBillingPage />} />
          <Route path="ops" element={<OpsPage />} />
          <Route path="sftp" element={<SftpAccountsPage />} />
          <Route path="sftp/browser" element={<SftpBrowserPage />} />
          <Route path="channels-playground" element={<ChannelsPlaygroundLayout />}>
            <Route index element={<Navigate to="api" replace />} />
            <Route path="api" element={<ChannelsPlaygroundApiPage />} />
            <Route path="widget" element={<ChannelsPlaygroundWidgetPage />} />
          </Route>
          <Route path="*" element={<Navigate to="/dealerships" replace />} />
        </Route>
      </Routes>
      <Toaster />
    </>
  );
}
